'use client';

import { buildWebClickProperties } from '@nextmar/snowplow-sdk';
import { captureAnalyticsEvent } from '@/reporting/earlyEvents';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';

/** PageView 路由去重时忽略的内部登录参数。 */
const PAGE_VIEW_IGNORED_QUERY_PARAMS = ['showLogin', 'from', 'reason'] as const;

/** 每个 document 的路由去重状态，SSR 不创建共享用户队列。 */
const pageViews = new WeakMap<Document, { routeKey: string }>();
/** 每个 document 的轻量点击监听只有一个，重复 Hook 通过引用计数共享。 */
const clickListeners = new WeakMap<
  Document,
  { count: number; handler: (event: MouseEvent) => void }
>();

/** 提前安装点击捕获器，仅提取纯属性并交给门面，不启动 tracker 自动监听。 */
function subscribeEarlyClicks(): () => void {
  const targetDocument = document;
  let state = clickListeners.get(targetDocument);
  if (!state) {
    /** 点击发生当时冻结元素和坐标；单个 DOM 异常不影响业务点击。 */
    const handler = (event: MouseEvent): void => {
      try {
        const properties = buildWebClickProperties(event);
        if (properties) captureAnalyticsEvent({ kind: 'webClick', properties });
      } catch {
        // 受限 DOM 或门面拒绝不会阻断原生事件传播。
      }
    };
    state = { count: 0, handler };
    targetDocument.addEventListener('click', handler, true);
    clickListeners.set(targetDocument, state);
  }
  state.count += 1;
  let disposed = false;
  /** 最后一个订阅卸载时才释放同一 capture listener。 */
  return () => {
    if (disposed) return;
    disposed = true;
    state.count -= 1;
    if (state.count === 0) {
      targetDocument.removeEventListener('click', state.handler, true);
      clickListeners.delete(targetDocument);
    }
  };
}

/** 浏览器当前路由去重所需的最小快照。 */
interface BrowserRouteSnapshot {
  pathname: string;
  search: string;
}

/** 去除 search 中可能混入的 hash，避免锚点变化触发 PageView。 */
function removeHashFragment(search: string): string {
  return search.split('#')[0] ?? '';
}

/** 过滤内部登录参数，同时保留其他业务 query。 */
function normalizePageViewSearch(search: string): string {
  const cleanSearch = removeHashFragment(search);

  // 空查询无需创建 URLSearchParams。
  if (!cleanSearch) return '';

  const queryText = cleanSearch.startsWith('?') ? cleanSearch.slice(1) : cleanSearch;

  // 只有问号时仍视为无查询。
  if (!queryText) return '';

  const params = new URLSearchParams(queryText);

  // 始终走同一序列化分支，避免 ignored 参数出现前后的 %20/+ 编码差异。
  PAGE_VIEW_IGNORED_QUERY_PARAMS.forEach((paramName) => {
    params.delete(paramName);
  });

  // URLSearchParams 保留其他业务参数的顺序与重复值。
  return params.toString();
}

/** 基于同一时刻的 pathname 与 search 构建 PageView route key。 */
function buildPageViewRouteKey(
  fallbackPathname: string | null,
  snapshot: BrowserRouteSnapshot | null,
): string | null {
  const pathname = snapshot?.pathname || fallbackPathname;

  // pathname 尚未就绪时不安排 PageView。
  if (!pathname) return null;

  const normalizedSearch = normalizePageViewSearch(snapshot?.search ?? '');

  // 没有业务 query 时仅使用 pathname。
  if (!normalizedSearch) return pathname;

  return `${pathname}${normalizedSearch.startsWith('?') ? normalizedSearch : `?${normalizedSearch}`}`;
}

/** 读取同一次浏览器 location 的 pathname 与 search。 */
function getBrowserRouteSnapshot(): BrowserRouteSnapshot | null {
  // SSR 环境不访问浏览器全局。
  if (typeof window === 'undefined') return null;

  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/** 在首屏和 Next 客户端路由变化时捕获原生 PageView 与早期自动点击。 */
export function useSnowplowSdkLifecycle(): void {
  const pathname = usePathname();

  /** 捕获当前规范化 route key，并立即交给门面保存原生 PV。 */
  const trackCurrentPageView = useCallback((): void => {
    if (typeof document === 'undefined') return;
    const routeKey = buildPageViewRouteKey(pathname, getBrowserRouteSnapshot());
    if (!routeKey || pageViews.get(document)?.routeKey === routeKey) return;
    // 新访问即使被拒绝也不能让旧 A 的 accepted 状态抑制 A→B→A。
    pageViews.delete(document);
    try {
      const result = captureAnalyticsEvent({ kind: 'pageView' });
      if (result.accepted) pageViews.set(document, { routeKey });
    } catch {
      // 同步失败保留下一次路由通知的重试机会，不影响渲染。
    }
  }, [pathname]);

  // 首屏和 usePathname 变化均检查当前 route key。
  useEffect(() => {
    trackCurrentPageView();
  }, [trackCurrentPageView]);

  // 复用 PathnameTracker 已派发的历史事件，不再次包装 history 方法。
  useEffect(() => {
    if (typeof window === 'undefined') return;

    /** 将历史事件转换为一次当前 route key 检查。 */
    const handleRouteChange = (): void => {
      trackCurrentPageView();
    };

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('zl-history-change', handleRouteChange);

    /** 卸载当前 Hook 时移除两个浏览器历史监听。 */
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('zl-history-change', handleRouteChange);
    };
  }, [trackCurrentPageView]);

  // 首个 effect 即安装轻量 capture listener，与 SDK/身份就绪完全解耦。
  useEffect(() => {
    if (typeof document === 'undefined') return;
    try {
      return subscribeEarlyClicks();
    } catch {
      // 浏览器监听API异常不阻断独立的PageView或应用渲染。
      return;
    }
  }, []);
}
